package dev.wyns.ventrova.app

import dev.wyns.ventrova.data.model.GeoPoint
import dev.wyns.ventrova.data.model.MenuItem
import dev.wyns.ventrova.data.model.OpenStatus
import dev.wyns.ventrova.data.model.Place
import dev.wyns.ventrova.data.model.PlaceCategory
import dev.wyns.ventrova.data.model.VeganLevel

/** Ghent city centre, shown when the user doesn't share their location. */
val DefaultCenter = GeoPoint(latitude = 51.0543, longitude = 3.7240)
const val DefaultZoom = 15.5

/** Radius options in kilometers; null means all distances. */
val RadiusOptions: List<Double?> = listOf(null, 0.5, 1.0, 2.0)

/** The three presets of the "How plant-based?" slider, from least to most strict. */
enum class StrictnessStop(val levels: Set<VeganLevel>) {
    VeganOptions(emptySet()),
    Vegetarian(setOf(VeganLevel.Vegan, VeganLevel.Vegetarian)),
    Vegan(setOf(VeganLevel.Vegan)),
}

data class Filters(
    /** Selected levels; empty means all levels. */
    val levels: Set<VeganLevel> = emptySet(),
    val openNow: Boolean = false,
    /** Maximum distance from the user (or map center) in kilometers; null means all distances. */
    val radiusKm: Double? = null,
    /** Selected kinds of place; empty means all. */
    val categories: Set<PlaceCategory> = emptySet(),
) {
    /** The slider preset matching [levels], or null for a custom selection. */
    val strictness: StrictnessStop? get() = StrictnessStop.entries.firstOrNull { it.levels == levels }

    /** Number of active filters, as shown on the "Filters" button. */
    val activeCount: Int
        get() = listOf(levels.isNotEmpty(), openNow, radiusKm != null, categories.isNotEmpty()).count { it }

    /** Active filters that live only in the filter sheet (not as quick chips). */
    val sheetOnlyCount: Int
        get() = listOf(strictness != StrictnessStop.VeganOptions, radiusKm != null, categories.isNotEmpty()).count { it }

    val isEmpty: Boolean get() = activeCount == 0

    fun matches(item: PlaceItem, ignoreLevels: Boolean = false, ignoreCategories: Boolean = false): Boolean {
        val place = item.place
        if (!ignoreLevels && levels.isNotEmpty() && place.veganLevel !in levels) return false
        if (!ignoreCategories && categories.isNotEmpty() && place.category !in categories) return false
        if (openNow && item.openStatus?.isOpen != true) return false
        val radius = radiusKm
        if (radius != null && (item.distanceMeters ?: Double.MAX_VALUE) > radius * 1000) return false
        return true
    }
}

/** A place with everything the UI needs to show it right now. */
data class PlaceItem(
    val place: Place,
    /** Distance from the user, or from the map center when the location is unknown. */
    val distanceMeters: Double?,
    /** Null when the opening hours are unknown. */
    val openStatus: OpenStatus?,
    val isFavorite: Boolean,
) {
    val id: String get() = place.id
}

data class SuggestionForm(
    val name: String = "",
    val address: String = "",
    val category: PlaceCategory = PlaceCategory.Restaurant,
    val level: VeganLevel? = null,
    val note: String = "",
    val triedToSend: Boolean = false,
    val sent: Boolean = false,
) {
    val canSend: Boolean get() = name.isNotBlank() && address.isNotBlank() && level != null
    val showError: Boolean get() = triedToSend && !canSend
}

/** Short-lived message, shown as a toast or snackbar. */
data class AppMessage(val kind: Kind, val id: Long) {
    enum class Kind { FavoriteSaved, FavoriteRemoved, LocationOff, YourLocation, NotAvailableYet, FryOilCheckRequested }
}

data class AppUiState(
    /** True until the places in the visible area have been loaded for the first time. */
    val isLoading: Boolean = true,
    val favoritesLoading: Boolean = true,
    /** True while the results don't belong to the current query yet. */
    val isSearching: Boolean = false,
    val showOnboarding: Boolean = false,
    val filters: Filters = Filters(),
    /** Filtered places in the visible map area, nearest first. */
    val nearby: List<PlaceItem> = emptyList(),
    /** Places in the visible map area that match all filters except the level filter. */
    val levelCounts: Map<VeganLevel, Int> = emptyMap(),
    /** Places in the visible map area that match all filters except the category filter. */
    val categoryCounts: Map<PlaceCategory, Int> = emptyMap(),
    val selected: PlaceItem? = null,
    val detail: PlaceItem? = null,
    /** Dishes of [detail]; null while loading, empty when the place has no analysed menu. */
    val detailMenu: List<MenuItem>? = null,
    val favorites: List<PlaceItem> = emptyList(),
    val query: String = "",
    val searchResults: List<PlaceItem> = emptyList(),
    val userLocation: GeoPoint? = null,
    /** Point distances are measured from: the user, or the map center. */
    val referencePoint: GeoPoint = DefaultCenter,
    val suggestion: SuggestionForm = SuggestionForm(),
    val message: AppMessage? = null,
)
